<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import { formatedDate } from "@/utils/proposalValidation";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "CPTemp1",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        projects: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    computed: {
        projectPages() {
            const pages = [];
            for (let i = 0; i < this.projects.projects.length; i += 2) {
                pages.push(this.projects.projects.slice(i, i + 2));
            }
            return pages;
        }
    },
    methods: {
        formatedDate
    }
};
</script>

<template>
  <div class="main-container" :style="{
    gap: pageGap ? '20px' : '0px',
    '--header-color': projects.style.colorScheme.header,
    '--body-color': projects.style.colorScheme.body,
    '--footer-color': projects.style.colorScheme.footer,
    '--heading-color': projects.style.heading.color,
    '--heading-size': projects.style.heading.size + 'px',
    '--heading-font': projects.style.heading.font,
    '--heading-weight': projects.style.heading.bold ? 'bold' : 'normal',
    '--heading-style': projects.style.heading.italic ? 'italic' : 'normal',
    '--heading-decoration': projects.style.heading.underline
      ? 'underline'
      : 'none',
    '--label-color': projects.style.label.color,
    '--label-size': projects.style.label.size + 'px',
    '--label-font': projects.style.label.font,
    '--label-weight': projects.style.label.bold ? 'bold' : 'normal',
    '--label-style': projects.style.label.italic ? 'italic' : 'normal',
    '--label-decoration': projects.style.label.underline
      ? 'underline'
      : 'none',
    '--text-color': projects.style.text.color,
    '--text-size': projects.style.text.size + 'px',
    '--text-font': projects.style.text.font,
    '--text-weight': projects.style.text.bold ? 'bold' : 'normal',
    '--text-style': projects.style.text.italic ? 'italic' : 'normal',
    '--text-decoration': projects.style.text.underline ? 'underline' : 'none',
  }">
    <div v-for="(page, pageIndex) in projectPages" :key="pageIndex" class="sub-container page-unit">
      <!-- HEADER -->
      <div class="banner header-banner">
        <div class="header-title">
          <SpellCheckedText :value="projects.title" @input="projects.title = $event" />
        </div>
      </div>

      <!-- PROJECT GRID -->
      <div class="compare-container">
        <div class="comp-grid">
          <div v-for="project in page" :key="project.id" class="comp-box">
            <div class="comp-info">
              <PhotoBox class="photobox" :project="project" height="300px" />
              <h2 class="project-name">
                <SpellCheckedText :value="project.projectName || 'N/A'" @input="project.projectName = $event" />
              </h2>

              <div class="info-sec">
                <p class="info-row">
                  <span class="location">
                    <SpellCheckedText :value="project.city || 'N/A'" @input="project.city = $event" />,
                    <SpellCheckedText :value="project.stateProvince || 'N/A'" @input="project.stateProvince = $event" />
                  </span>
                </p>
                <p class="info-row">
                  <span class="label">Client: </span>
                  <span class="value">
                    <SpellCheckedText :value="project.clientAccountName || 'N/A'"
                      @input="project.clientAccountName = $event" />
                  </span>
                </p>
                <p class="info-row">
                  <span class="label">Designers: </span>
                  <span class="value">
                    <span v-if="!project.designerAccounts || project.designerAccounts.length == 0">N/A</span>
                    <span v-else v-for="(designer, index) in project.designerAccounts" :key="index">
                      <SpellCheckedText :value="designer.accountName || 'N/A'" @input="designer.accountName = $event" />
                      <span v-if="index !== project.designerAccounts.length - 1">,</span>
                    </span>
                  </span>
                </p>
                <p class="info-row">
                  <span class="label">Construction Cost: </span>
                  <span class="value">
                    {{ project.value ? `$${project.value.toLocaleString()}` : 'N/A' }}</span>
                </p>
                <p class="info-row">
                  <span class="label">Construction Duration: </span>
                  <span class="value">{{ formatedDate(project.constructionStartDate) || 'N/A' }} to {{
                    formatedDate(project.constructionCompletionDate) || 'N/A'
                  }}</span>
                </p>
              </div>

              <div class="about-sec">
                <p class="about-text">
                  <SpellCheckedText :value="project.projectDetails || ''" @input="project.projectDetails = $event" />
                </p>
              </div>

              <div v-if="project.projectTeam && project.projectTeam.length > 0" class="team-list">
                <div v-for="(member, mIdx) in project.projectTeam" :key="mIdx" class="team-member">
                  <div class="member-avatar photobox">
                    <PhotoBox class="photobox" :project="member" :bgColor="projects.style.colorScheme.body"
                      type="profile" />
                  </div>
                  <div class="member-text">
                    <p class="mb-0">
                      <SpellCheckedText :value="member.name" @input="member.name = $event" />
                      <span v-if="member.role"> - </span>
                    </p>
                    <p class="mb-0" v-if="member.role">
                      <SpellCheckedText :value="member.role" @input="member.role = $event" />
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Add an empty box if this page has only 1 project -->
          <div v-if="page.length === 1" class="comp-box"></div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="banner footer-banner">
        <span class="page-number">{{ String(startPage + pageIndex).padStart(2, '0') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main-container {
  display: flex;
  flex-direction: column;
  width: 211mm;
  min-height: 296.83mm;
  position: relative;
  box-sizing: border-box;

  &:hover {
    .photobox :deep(.photo-overlay) {
      opacity: 1;
    }

    .photobox :deep(img) {
      opacity: 0.9;
    }
  }

  .sub-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: white;
    height: 296.83mm;

    .banner {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60px;
      position: relative;

      &.header-banner {
        background: var(--header-color);
      }

      &.footer-banner {
        background: var(--footer-color);
      }

      .header-title {
        font-size: 18px;
        color: white;
        margin-bottom: 0px;
        font-weight: 700;
        font-family: Helvetica;
      }

      .page-number {
        position: absolute;
        right: 15px;
        bottom: 10px;
        font-size: 14px;
        font-weight: 700;
        color: white;
        font-family: Helvetica;
      }
    }

    .compare-container {
      flex: 1;
      padding: 10px 20px;
      overflow: hidden;

      .comp-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 3px;
        background: white;
        align-items: stretch;
        height: 100%;
        border-top: 10px solid var(--body-color);
        border-bottom: 10px solid var(--body-color);

        .comp-box {
          flex: 1;
          display: flex;
          overflow: hidden;
          background: var(--body-color);
          height: 100%;

          .comp-info {
            display: flex;
            flex-direction: column;
            padding: 0px 10px;
            width: 100%;
            height: 100%;
			text-align:left;

            .project-name {
              color: var(--heading-color);
              font-size: var(--heading-size);
              font-weight: var(--heading-weight);
              font-family: var(--heading-font);
              font-style: var(--heading-style);
              text-decoration: var(--heading-decoration);
              margin: 5px 0px;
            }

            .info-sec {
              margin-bottom: 15px;

              .info-row {
                margin: 0px;
                line-height: 1.2;

                .location {
                  color: var(--label-color);
                  font-size: var(--label-size);
                  font-weight: var(--label-weight);
                  font-family: var(--label-font);
                  font-style: var(--label-style);
                  text-decoration: var(--label-decoration);
                  margin: 0px;
                }

                .label {
                  color: var(--label-color);
                  font-size: var(--label-size);
                  font-weight: var(--label-weight);
                  font-family: var(--label-font);
                  font-style: var(--label-style);
                  text-decoration: var(--label-decoration);
                }

                .value {
                  color: var(--text-color);
                  font-size: var(--text-size);
                  font-weight: var(--text-weight);
                  font-family: var(--text-font);
                  font-style: var(--text-style);
                  text-decoration: var(--text-decoration);
                }
              }
            }

            .about-sec {
              position: relative;
              flex: 1;
              overflow: hidden;
            }

            .team-list {
              display: flex;
              flex-direction: column;
              gap: 5px;
              margin-top: 5px;
              flex-shrink: 0;

              .team-member {
                display: flex;
                align-items: center;
                gap: 5px;

                .member-avatar {
                  width: 45px;
                  height: 45px;
                  border-radius: 50%;
                  overflow: hidden;
                  background: #eee;
                  flex-shrink: 0;
                  background-color: var(--body-color);

                  &:hover {
                    .photobox :deep(.photo-overlay) {
                      gap: 0px;
                    }

                    .photobox :deep(.overlay-btn) {
                      width: 20px;
                      height: 20px;
                    }
                  }
                }

                .member-text {
                  font-size: var(--text-size);
                  color: var(--text-color);
                  font-weight: var(--text-weight);
                  font-family: var(--text-font);
                  font-style: var(--text-style);
                  text-decoration: var(--text-decoration);
                  margin: 0;
                }
              }
            }


            .about-text {
              color: var(--text-color);
              font-size: var(--text-size);
              font-weight: var(--text-weight);
              font-family: var(--text-font);
              font-style: var(--text-style);
              text-decoration: var(--text-decoration);
              margin: 0;
              height: 100%;
              overflow: hidden;
              white-space: pre-wrap;
            }
          }
        }
      }
    }
  }
}
</style>
